import { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Circle, Polyline, Tooltip as LTooltip, useMap } from 'react-leaflet';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../lib/mapTiles';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

export interface LatLng { lat: number; lng: number }

// Coloured SVG pins (Leaflet's default marker images don't resolve under bundlers).
const pin = (color: string) => L.divIcon({
  className: '',
  iconSize: [26, 26],
  iconAnchor: [13, 26],
  html: `<svg viewBox="0 0 24 32" width="26" height="26" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z" fill="${color}" stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="5" fill="white"/></svg>`,
});
const START_ICON = pin('#1677FF'); // blue
const LEAVE_ICON = pin('#FA8C16'); // orange
const SITE_ICON = pin('#52C41A');  // green

/** Great-circle distance between two coordinates, in metres. */
export function distanceMeters(a: LatLng, b: LatLng): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export const fmtDist = (m: number) => (m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(2)} km`);

// Leaflet mis-measures inside an animated Drawer — recompute size, then frame all points.
function FitBounds({ points }: { points: LatLng[] }) {
  const map = useMap();
  const key = points.map((p) => `${p.lat},${p.lng}`).join('|');
  useEffect(() => {
    const t = setTimeout(() => {
      map.invalidateSize();
      if (points.length === 1) {
        map.setView([points[0].lat, points[0].lng], 16);
      } else if (points.length > 1) {
        map.fitBounds(points.map((p) => [p.lat, p.lng]) as any, { padding: [30, 30], maxZoom: 17 });
      }
    }, 200);
    return () => clearTimeout(t);
  }, [map, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

interface Props {
  start?: LatLng | null;
  leave?: LatLng | null;
  site?: LatLng | null;
  /** Geofence radius (m) drawn around the customer location. */
  geofenceRadius?: number;
  height?: number;
}

/**
 * Per-visit mini map: the rep's Start (check-in) and Leave (check-out) points,
 * plus the customer's registered location with its geofence circle for reference.
 * Renders nothing when no coordinates are available.
 */
export function VisitRouteMap({ start, leave, site, geofenceRadius = 100, height = 240 }: Props) {
  const points = [start, leave, site].filter(Boolean) as LatLng[];
  if (!points.length) return null;

  return (
    <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #eee' }}>
      <MapContainer
        center={[points[0].lat, points[0].lng]}
        zoom={15}
        style={{ height, width: '100%' }}
        scrollWheelZoom
      >
        <TileLayer attribution={MAP_TILE_ATTRIBUTION} url={MAP_TILE_URL} />
        <FitBounds points={points} />

        {site && (
          <Circle
            center={[site.lat, site.lng]}
            radius={geofenceRadius}
            pathOptions={{ color: '#52C41A', fillColor: '#52C41A', fillOpacity: 0.08, weight: 1 }}
          />
        )}
        {start && leave && (
          <Polyline
            positions={[[start.lat, start.lng], [leave.lat, leave.lng]]}
            pathOptions={{ color: '#1677FF', dashArray: '6 6', weight: 2 }}
          />
        )}

        {site && (
          <Marker position={[site.lat, site.lng]} icon={SITE_ICON}>
            <LTooltip>Customer</LTooltip>
          </Marker>
        )}
        {start && (
          <Marker position={[start.lat, start.lng]} icon={START_ICON}>
            <LTooltip>Visit start</LTooltip>
          </Marker>
        )}
        {leave && (
          <Marker position={[leave.lat, leave.lng]} icon={LEAVE_ICON}>
            <LTooltip>Visit end</LTooltip>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
}
